import React, { useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "../styles/quill.css";
import { cn } from "../lib/utils";

const RichTextEditor = ({
  value,
  onChange,
  mustFill = false,
  description = "",
  validationError = false,
  className = "",
  ...props
}) => {
  const modules = useMemo(
    () => ({
      toolbar: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike', 'link', 'image', 'video', {'color': []}, { 'background': [] }, { 'list': 'ordered'}, { 'list': 'bullet' }, { 'indent': '-1'}, { 'indent': '+1' }, { 'align': [] }, 'blockquote', 'code-block'],
      ],
      keyboard: {
        bindings: {
          tab: false,
          'shift-tab': false
        }
      }
    }),
    []
  );

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'link', 'image', 'video',
    'blockquote', 'code-block'
  ];

  return (
    <div className={cn("relative group", className)}>
      <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        className={cn(
          "quill-editor",
          validationError ? "error" : "",
          "rounded-lg transition-all duration-200",
          validationError 
            ? "border-red-500 shadow-red-100" 
            : "border-gray-400 focus-within:border-blue-500",
          // Base styles
          "border-0 bg-white",
          // Toolbar base styling
          "[&_.ql-toolbar]:border-0 [&_.ql-toolbar]:bg-slate-200",
          "[&_.ql-toolbar]:rounded-t-lg [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-gray-400",
          "[&_.ql-toolbar]:p-0 [&_.ql-toolbar]:flex [&_.ql-toolbar]:flex-col [&_.ql-toolbar]:lg:flex-row [&_.ql-toolbar]:lg:justify-start [&_.ql-toolbar]:items-center [&_.ql-toolbar]:justify-center [&_.ql-toolbar]:gap-0",
          // Format groups
          "[&_.ql-formats]:flex [&_.ql-formats]:items-center [&_.ql-formats]:gap-0 [&_.ql-formats]:m-0 [&_.ql-formats]:bg-red-5 [&_.ql-formats]:w-full [&_.ql-formats]:lg:w-auto",
          "[&_.ql-formats]:py-0 [&_.ql-formats]:px-0 [&_.ql-formats]:rounded-md",
          // Button styling
          "[&_.ql-toolbar_button]:h-8 [&_.ql-toolbar_button]:w-8",
          "[&_.ql-toolbar_button]:flex [&_.ql-toolbar_button]:items-center [&_.ql-toolbar_button]:justify-center",
          "[&_.ql-toolbar_button]:rounded-md [&_.ql-toolbar_button]:transition-colors",
          "[&_.ql-toolbar_button]:text-gray-700",
          "[&_.ql-toolbar_button]:text-base",
          "[&_.ql-toolbar_button:hover]:bg-gray-100 [&_.ql-toolbar_button:hover]:text-gray-900",
          "[&_.ql-toolbar_button.ql-active]:bg-blue-50 [&_.ql-toolbar_button.ql-active]:text-blue-600",
          // Picker styling
          "[&_.ql-toolbar_.ql-picker]:h-6 [&_.ql-toolbar_.ql-picker]:bg-green-0 [&_.ql-toolbar_.ql-picker]:-mt-1",
          "[&_.ql-toolbar_.ql-picker-label]:h-8 [&_.ql-toolbar_.ql-picker-label]:min-w-9 [&_.ql-toolbar_.ql-picker-label]:-mt-1",
          "[&_.ql-toolbar_.ql-picker-label]:text-gray-700 [&_.ql-toolbar_.ql-picker-label]:text-base",
          "[&_.ql-toolbar_.ql-picker-label]:flex [&_.ql-toolbar_.ql-picker-label]:items-center [&_.ql-toolbar_.ql-picker-label]:justify-start [&_.ql-toolbar_.ql-picker-label]:hover:text-gray-900",
          "[&_.ql-toolbar_.ql-picker.ql-expanded_.ql-picker-label]:bg-blue-50 [&_.ql-toolbar_.ql-picker.ql-expanded_.ql-picker-label]:text-blue-600",
          // Dropdown menu
          "[&_.ql-toolbar_.ql-picker-options]:bg-white [&_.ql-toolbar_.ql-picker-options]:rounded-md",
          "[&_.ql-toolbar_.ql-picker-options]:shadow-lg [&_.ql-toolbar_.ql-picker-options]:border [&_.ql-toolbar_.ql-picker-options]:border-gray-200",
          "[&_.ql-toolbar_.ql-picker-item]:px-3 [&_.ql-toolbar_.ql-picker-item]:py-2 [&_.ql-toolbar_.ql-picker-item]:text-base",
          "[&_.ql-toolbar_.ql-picker-item]:hover:bg-gray-50",
          // Separator styling
          "[&_.ql-toolbar_.ql-separator]:h-7 [&_.ql-toolbar_.ql-separator]:w-px [&_.ql-toolbar_.ql-separator]:bg-gray-200 [&_.ql-toolbar_.ql-separator]:mx-2",
          // Container styling
          "[&_.ql-container]:border-0 [&_.ql-container]:bg-slate-100 [&_.ql-container]:rounded-b-lg",
          // Editor styling
          "[&_.ql-editor]:min-h-[200px] [&_.ql-editor]:p-4 [&_.ql-editor]:text-base",
          "[&_.ql-editor_p]:leading-relaxed [&_.ql-editor_p]:mb-3",
          "[&_.ql-editor_h1]:text-4xl [&_.ql-editor_h1]:font-bold [&_.ql-editor_h1]:mb-4",
          "[&_.ql-editor_h2]:text-3xl [&_.ql-editor_h2]:font-bold [&_.ql-editor_h2]:mb-3",
          "[&_.ql-editor_h3]:text-2xl [&_.ql-editor_h3]:font-bold [&_.ql-editor_h3]:mb-3",
          "[&_.ql-editor_ul]:list-disc [&_.ql-editor_ul]:ml-6 [&_.ql-editor_ul]:mb-3",
          "[&_.ql-editor_ol]:list-decimal [&_.ql-editor_ol]:ml-6 [&_.ql-editor_ol]:mb-3",
          "[&_.ql-editor_blockquote]:border-l-4 [&_.ql-editor_blockquote]:border-gray-300 [&_.ql-editor_blockquote]:pl-4 [&_.ql-editor_blockquote]:italic [&_.ql-editor_blockquote]:text-gray-600 [&_.ql-editor_blockquote]:mb-3",
          "[&_.ql-editor_code-block]:bg-gray-100 [&_.ql-editor_code-block]:p-3 [&_.ql-editor_code-block]:rounded [&_.ql-editor_code-block]:font-mono [&_.ql-editor_code-block]:text-sm [&_.ql-editor_code-block]:mb-3"
        )}
      />
      {description && (
        <p className="mt-2 text-sm text-gray-500 leading-relaxed">{description}</p>
      )}
    </div>
  );
};

export default RichTextEditor; 